"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { PenNamePrompt } from "@/components/editor/pen-name-prompt";
import { getPenNameReminderStatus } from "@/lib/actions/profile-actions";
import { useAuth } from "@/providers/auth-provider";
import { PROFILE_NAME_CHANGED_EVENT, type ProfileNameChangedDetail } from "@/lib/profile/client-events";
type ReminderStatus = Awaited<ReturnType<typeof getPenNameReminderStatus>>;
interface LoadedReminderStatus {
  userId: string;
  status: ReminderStatus;
}
export function PenNameReminder() {
  const $ = _c(14);
  const pathname = usePathname();
  const {
    user,
    loading
  } = useAuth();
  const [loaded, setLoaded] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const confirmedUserIdsRef = useRef(t0);
  const userId = user?.id ?? null;
  let t1;
  let t2;
  if ($[1] !== loading || $[2] !== userId) {
    t1 = () => {
      if (loading || !userId) {
        return;
      }
      let isCurrent = true;
      getPenNameReminderStatus().then(result => {
        if (isCurrent && (!result || result.userId === userId)) {
          setLoaded({
            userId,
            status: result && confirmedUserIdsRef.current.has(userId) ? {
              ...result,
              shouldPrompt: false
            } : result
          });
        }
      }).catch(() => {
        if (isCurrent) {
          setLoaded({
            userId,
            status: null
          });
        }
      });
      return () => {
        isCurrent = false;
      };
    };
    t2 = [loading, userId];
    $[1] = loading;
    $[2] = userId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== userId) {
    t3 = () => {
      const handleProfileNameChanged = function handleProfileNameChanged(event) {
        const detail = (event as CustomEvent<Partial<ProfileNameChangedDetail>>).detail;
        if (detail?.userId !== userId) {
          return;
        }
        confirmedUserIdsRef.current.add(userId);
        setLoaded(current => current?.userId === userId && current.status ? {
          ...current,
          status: {
            ...current.status,
            shouldPrompt: false
          }
        } : current);
      };
      window.addEventListener(PROFILE_NAME_CHANGED_EVENT, handleProfileNameChanged);
      return () => window.removeEventListener(PROFILE_NAME_CHANGED_EVENT, handleProfileNameChanged);
    };
    t4 = [userId];
    $[5] = userId;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const status = loaded?.userId === userId ? loaded.status : null;
  if (!status?.shouldPrompt) {
    return null;
  }
  const t5 = `${status.userId}:${pathname}`;
  let t6;
  if ($[8] !== status) {
    t6 = () => setLoaded({
      userId: status.userId,
      status: {
        ...status,
        shouldPrompt: false
      }
    });
    $[8] = status;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== status.displayName || $[11] !== t5 || $[12] !== t6) {
    t7 = <PenNamePrompt key={t5} initialPenName={status.displayName} onSaved={t6} />;
    $[10] = status.displayName;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}